import clsx from "clsx";
import React from "react";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { Text } from "web/react/emo/text/text";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import { useProductCardContext } from "./product-card";
import styles from "./product-card-details.module.css";
import ProductCardLink from "./product-card-link";

interface ProductCardDetailsProps {
    showVariant?: boolean;
    className?: string;
    feedType?: string;
    analyticsEventLabel?: string;
    hideShortDescription?: boolean | string;
}

const defaultProps = {
    showVariant: false,
};

function getPromoLabelText(promoLabel: string | null | undefined): string | undefined {
    if (promoLabel) {
        if (promoLabel === "out_of_stock") {
            return gettext("oos_product.oos.label");
        } else if (promoLabel === "low_stock") {
            return gettext("product_card.promo_label.low_stock");
        } else if (promoLabel === "sale") {
            return gettext("product_card.promo_label.sale");
        } else if (promoLabel === "trending") {
            return gettext("product_card.promo_label.trending");
        }
    }
    return undefined;
}

function ProductCardDetails({
    showVariant,
    className,
    feedType,
    analyticsEventLabel,
}: ProductCardDetailsProps = defaultProps): React.ReactElement {
    const {
        designer_name: designerName,
        short_description: shortDescription,
        full_price_with_currency_symbol: fullPriceWithCurrencySymbol,
        has_variant: hasVariant,
        in_stock: inStock,
        show_retailer_as_affiliate: showRetailerAsAffiliate,
        retailer_name: retailerName,
        context_type: contextType,
        promo_label: promoLabel,
        id: productId,
    } = useProductCardContext();

    const useFeedRedesign = environment.getFeature("cw_feeds_redesign");
    const promoLabelText = getPromoLabelText(promoLabel);

    function handleShortDescriptionClick(): void {
        analytics.event("product_card", "short description", analyticsEventLabel, false, {
            product_id: productId,
        });
    }

    function handleRetailerClick(): void {
        analytics.event("product_card", "retailer", analyticsEventLabel, false, {
            product_id: productId,
        });
    }

    const productCardDescription = (
        <>
            <div className={styles.designer}>{designerName}</div>
            {!!shortDescription && (
                <div className={styles.shortDescription} onClick={handleShortDescriptionClick}>
                    {shortDescription}
                </div>
            )}
        </>
    );

    // ORG-3540: Render lead link for short description if in the new related section
    const seoElevateSimilarItems = environment.getFeature("seo_elevate_similar_items_in_oos_pdp");

    return (
        <div className={clsx(styles.details, className)}>
            <ProductCardLink
                reason="text"
                className={styles.detailsLink}
                feedType={feedType}
                predicate={!!(seoElevateSimilarItems && contextType === "related-new-product")}
            >
                <h3
                    className={styles.detailsWrapper}
                    aria-description={gettext("product_card.product_description.label")}
                >
                    {productCardDescription}
                </h3>
            </ProductCardLink>
            <div className={styles.priceSection}>
                {fullPriceWithCurrencySymbol ? (
                    <ProductCardPriceSection />
                ) : (
                    <ProductCardNotAvailable />
                )}
            </div>
            {hasVariant && inStock && showVariant && (
                <p className={styles.variants}>
                    <em>{gettext("product_card.more_colors_available")}</em>
                </p>
            )}
            <div className={styles.retailer} onClick={handleRetailerClick}>
                {showRetailerAsAffiliate && (
                    <>
                        <SVGIcon name="hyperlink" />{" "}
                    </>
                )}
                <span>{retailerName}</span>
            </div>
            {useFeedRedesign && promoLabelText && (
                <Text textStyle={"caption-2"} upperCase={true} color={"secondaryBrand"}>
                    {promoLabelText}
                </Text>
            )}
        </div>
    );
}

export function ProductCardPriceSection(): React.ReactElement {
    const {
        full_price_with_currency_symbol: fullPriceWithCurrencySymbol,
        sale_price_with_currency_symbol: salePriceWithCurrencySymbol,
        in_stock: inStock,
        free_shipping: freeShipping,
        on_promotion: onPromotion,
        sale_discount: saleDiscount,
        discount_info: discountInfo,
    } = useProductCardContext();

    if (!inStock) {
        return (
            <>
                <del className={clsx(styles.price)}>{fullPriceWithCurrencySymbol}</del>

                <span className={clsx(styles.soldOutMessage)}>
                    {gettext("product_card.sold_out")}
                </span>
            </>
        );
    }
    return (
        <>
            {freeShipping && <SVGIcon className={styles.icon} name="truck" />}

            {onPromotion && <SVGIcon className={styles.icon} name="tag" />}

            {saleDiscount ? (
                <>
                    <del className={styles.price}>{fullPriceWithCurrencySymbol}</del>
                    <span className={clsx(styles.price, styles.salePrice)}>
                        {salePriceWithCurrencySymbol}
                    </span>
                </>
            ) : (
                <span className={styles.price}>{fullPriceWithCurrencySymbol}</span>
            )}

            {discountInfo && <span className={styles.discountInfo}>{discountInfo}</span>}
        </>
    );
}

export function ProductCardNotAvailable(): React.ReactElement {
    return (
        <span className={styles.soldOutMessage}>
            {gettext("product_card.not_available_in_your_country")}
        </span>
    );
}

export default ProductCardDetails;
